import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  contentChildren,
  input,
  model,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TabComponent } from './tab.component';

/**
 * Tab strip component for organizing content into switchable tabs.
 * Supports both horizontal and vertical orientations with full accessibility.
 *
 * @example Basic horizontal tabs
 * ```html
 * <app-tab-strip [(selected)]="activeTab">
 *   <app-tab id="overview" label="Overview">
 *     <h2>Overview Content</h2>
 *   </app-tab>
 *   <app-tab id="details" label="Details">
 *     <h2>Details Content</h2>
 *   </app-tab>
 * </app-tab-strip>
 * ```
 *
 * @example Vertical tabs with custom tab labels
 * ```html
 * <app-tab-strip [(selected)]="activeTab" orientation="vertical">
 *   <app-tab id="dashboard" label="Dashboard">
 *     <ng-template #tabLabel>
 *       <i class="icon-dashboard"></i>
 *       <span>Dashboard</span>
 *     </ng-template>
 *     <!-- panel content -->
 *   </app-tab>
 *   <app-tab id="settings" label="Settings">
 *     <ng-template #tabLabel>
 *       <i class="icon-gear"></i>
 *       <span>Settings</span>
 *     </ng-template>
 *     <!-- panel content -->
 *   </app-tab>
 * </app-tab-strip>
 * ```
 *
 * @remarks
 * - Use two-way binding `[(selected)]` to track the active tab
 * - Each child `<app-tab>` must have a unique `id`
 * - Tabs can have custom content via `#tabLabel` template or simple text via `label` attribute
 * - Supports horizontal (default) and vertical orientations
 * - Fully accessible with ARIA attributes and semantic HTML
 * - All styling uses CSS variables for theming
 */
@Component({
  selector: 'app-tab-strip',
  standalone: true,
  templateUrl: './tab-strip.component.html',
  styleUrl: './tab-strip.component.css',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgTemplateOutlet],
})
export class TabStripComponent {
  /**
   * Layout orientation of the tab strip.
   * - 'horizontal': Tabs are arranged horizontally above the content (default)
   * - 'vertical': Tabs are arranged vertically to the left of the content
   * @default 'horizontal'
   */
  orientation = input<'horizontal' | 'vertical'>('horizontal');

  /**
   * Currently selected tab ID.
   * Use two-way binding [(selected)] to sync with parent component.
   *
   * @example
   * ```typescript
   * activeTab = 'overview';
   * ```
   * ```html
   * <app-tab-strip [(selected)]="activeTab">
   *   <app-tab id="overview" label="Overview">...</app-tab>
   * </app-tab-strip>
   * ```
   */
  selected = model<string>();

  /**
   * All tab children within this tab strip.
   * Automatically populated via content projection.
   */
  tabs = contentChildren(TabComponent);

  /**
   * Select a tab by its ID.
   * Updates the selected model and emits the change event.
   *
   * @param id - The unique ID of the tab to select
   */
  selectTab(id: string): void {
    this.selected.set(id);
  }

  /**
   * Check if a tab is currently selected
   * @param id - The unique ID of the tab to check
   */
  isSelected(id: string): boolean {
    return this.selected() === id;
  }
}
